'use client';

import { ChevronIcon } from '@fastfive-dev/fastfive-design-system/icons/arrow';
import {
  FloatingIcon,
  type FloatingIconProps,
} from '@fastfive-dev/fastfive-design-system/icons/etc';
import Link from 'next/link';
import { twMerge } from 'tailwind-merge';

import { GTM_EVENT } from '@/shared/constants/gtm';
import { gtmClick } from '@/shared/utils/gtm';

type Props = {
  label: string;
  iconType: FloatingIconProps['type'];
  /** primary: 오렌지 배경 + 화이트 아이콘/텍스트, plain: 화이트 배경 + 오렌지 아이콘. */
  tone: 'primary' | 'plain';
  /** 지정 시 링크로, 없으면 버튼으로 렌더. */
  href?: string;
  onClick?: () => void;
};

const BASE =
  'text-body-400 flex h-full min-w-px flex-1 items-center justify-center gap-4 px-10 py-14 font-semibold';

/** 모바일 하단 리드 바의 단일 항목. */
export function LeadBarItem({ label, iconType, tone, href, onClick }: Props) {
  const isPrimary = tone === 'primary';
  const className = twMerge(
    BASE,
    isPrimary ? 'bg-primary-600 text-gray-100' : 'bg-gray-100 text-gray-900',
  );
  const gtm = gtmClick(GTM_EVENT.FLOATING, label);

  const content = (
    <>
      <FloatingIcon
        type={iconType}
        value={isPrimary ? 'white' : 'orange'}
        className="shrink-0"
      />
      <span className="truncate">{label}</span>
      <ChevronIcon size={16} direction="right" className="shrink-0" />
    </>
  );

  if (href) {
    return (
      <Link href={href} aria-label={label} className={className} {...gtm}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className={className}
      {...gtm}
    >
      {content}
    </button>
  );
}
